<script setup lang="ts">
import { Form, useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import * as yup from 'yup'
import ErrorIcon from '/svg/error-icon.svg'

// import { createIranianAccountAction } from "@/api/functions/bank-account/create-bank-account.action";

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const loading = ref(false)

// Validation schema
const validationSchema = yup.object({
  sheba: yup
    .string()
    .required('شماره شبا الزامی است')
    .matches(/^[0-9]{24}$/, 'شماره شبا باید ۲۴ رقم باشد'),
  cardNumber: yup
    .string()
    .required('شماره کارت الزامی است')
    .length(16, 'شماره کارت باید ۱۶ رقمی باشد')
    .matches(/^[0-9]+$/, 'شماره کارت باید فقط عدد باشد'),
  bankName: yup
    .string()
    .required('نام بانک الزامی است')
    .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
    .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد'),
  ownerName: yup
    .string()
    .required('نام دارنده حساب الزامی است')
    .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
    .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد'),
  nationalCode: yup
    .string()
    .required('کد ملی الزامی است')
    .length(10, 'کد ملی باید ۱۰ رقمی باشد')
    .matches(/^[0-9]+$/, 'کد ملی باید فقط عدد باشد'),
  birthDate: yup.object({
    day: yup.string().required('روز تولد الزامی است'),
    month: yup.string().required('ماه تولد الزامی است'),
    year: yup.string().required('سال تولد الزامی است'),
  }),
})

// Form setup
const { errors } = useForm({
  validationSchema,
  initialValues: {
    paypalEmail: '',
    paypalId: '',
    iban: '',
    bic: '',
    bankName: '',
    ownerName: '',
    country: '',
    isPound: null,
    sortCode: '',
    accountNumber: '',
    revolutTag: '',
    sheba: '',
    cardNumber: '',
    nationalCode: '',
    birthDate: { day: '', month: '', year: '' },
  },
})

const { value: sheba } = useField('sheba')
const { value: cardNumber } = useField('cardNumber')
const { value: bankName } = useField('bankName')
const { value: ownerName } = useField('ownerName')
const { value: nationalCode } = useField('nationalCode')
const { value: birthDate } = useField<{ day: string; month: string; year: string }>('birthDate')

const dayOptions = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const monthOptions = computed(() =>
  Array.from({ length: 12 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const yearOptions = computed(() =>
  Array.from({ length: 104 }, (_, i) => ({
    label: (1403 - i).toString(),
    value: (1403 - i).toString(),
  }))
)

// Mutation
// const { mutate: createIranianAccount } = useMutationApi({
//     mutationKey: ["createIranianAccount"],
//     mutationFn: createIranianAccountAction,
//     onSuccess: () => {
//         loading.value = false;
//         toast.success("حساب مقصد ریالی با موفقیت ایجاد شد", "موفقیت");
//         emit("confirm");
//         closeModal();
//     },
//     onError: (error: Error) => {
//         loading.value = false;
//         toast.error(getErrorMessage(error), "خطا");
//     },
// });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const submitForm = () => {
  // loading.value = true;
  // const shebaWithIR = `IR${values.sheba}`;
  // const jalaaliDateString = `${values.birthDate.year}/${values.birthDate.month}/${values.birthDate.day}`;
  // const gregorianDate = moment(jalaaliDateString, "jYYYY/jMM/jDD").format(
  //     "YYYY-MM-DD"
  // );
  // createIranianAccount({
  //     sheba_number: shebaWithIR,
  //     card_number: values.cardNumber,
  //     bank_name: values.bankName,
  //     owner_full_name: values.ownerName,
  //     national_code: values.nationalCode,
  //     birth_date: gregorianDate,
  // });
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" background="#FFF"
    width="700px" :closable="true">
    <div class="px-5 py-4 pb-7 text-[#1b325a]">
      <p class="bg-[#f5f5f5] rounded-xl p-2.5 text-[#2D5396] text-base font-normal">
        حساب های مقصد ریالی: حساب بانکی که فروشنده قصد دارد تسویه ریالی توسط پی جیب، با آن حساب انجام شود.
      </p>

      <Form @submit="submitForm" class="space-y-6 mt-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Input v-model="sheba" name="sheba" :isSheba="true" label="شماره شبا را وارد کنید" type="text"
              placeholder="060120000000009886055009" />
            <div v-if="errors.sheba" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
              <img :src="ErrorIcon" width="15" />
              <span>{{ errors.sheba }}</span>
            </div>
          </div>

          <div>
            <Input v-model="cardNumber" name="cardNumber" label="شماره کارت را وارد کنید" type="text"
              placeholder="6104338650180389" />
            <div v-if="errors.cardNumber" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
              <img :src="ErrorIcon" width="15" />
              <span>{{ errors.cardNumber }}</span>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Input v-model="bankName" name="bankName" label="نام بانک را به انگلیسی وارد کنید" type="text"
              placeholder="ملی" />
            <div v-if="errors.bankName" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
              <img :src="ErrorIcon" width="15" />
              <span>{{ errors.bankName }}</span>
            </div>
          </div>

          <div>
            <Input v-model="ownerName" name="ownerName" label="نام دارنده حساب را وارد کنید" type="text"
              />
            <div v-if="errors.ownerName" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
              <img :src="ErrorIcon" width="15" />
              <span>{{ errors.ownerName }}</span>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Input v-model="nationalCode" name="nationalCode" label="کد ملی دارنده حساب را وارد کنید" type="text"
             />
            <div v-if="errors.nationalCode" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
              <img :src="ErrorIcon" width="15" />
              <span>{{ errors.nationalCode }}</span>
            </div>
          </div>

          <div>
            <div class="grid grid-cols-3 gap-4">
              <Select v-model="birthDate.day" name="birthDate.day" :options="dayOptions" label="روز تولد" />
              <Select v-model="birthDate.month" name="birthDate.month" :options="monthOptions" label="ماه تولد" />
              <Select v-model="birthDate.year" name="birthDate.year" :options="yearOptions" label="سال تولد" />
            </div>
            <div v-if="errors.birthDate" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
              <img :src="ErrorIcon" width="15" />
              <span>تاریخ تولد الزامی است</span>
            </div>
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <Button type="submit" :disabled="loading" class="px-6">
            {{ loading ? "در حال ثبت..." : "ثبت و افزودن حساب بانکی" }}
          </Button>
          <Button type="button" class="border-2 border-[#2d5396] text-[#2d5396] bg-transparent px-6 hover:bg-gray-100"
            @click="closeModal">
            انصراف
          </Button>
        </div>
      </Form>
    </div>
  </Modal>
</template>

<style scoped>
.close-modal-icon {
  display: none;
}
</style>
